import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import ErrorCard from "../ErrorCard";
import LoadingSpinner from "../LoadingSpinner";
import api from "../../services/api";
import {
  US_STATE_CODES,
  US_STATE_NAMES,
} from "../../constants/usStates";
import { US_STATE_FLAG_DATA } from "../../constants/usStateFlags";

const Container = styled.div`
  margin: 0 0 24px;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  color: var(--zyvgo-text-primary, #222222);
  font-size: 0.875rem;
  font-weight: 600;
`;

const Required = styled.span`
  color: #ef4444;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 14px 16px;
  border: 1px solid var(--zyvgo-border, #d7dce2);
  border-radius: var(--zyvgo-radius-md, 8px);
  background: #fff;
  color: var(--zyvgo-text-primary, #222222);
  font: inherit;
  outline: none;

  &:focus {
    border-color: var(--zyvgo-primary, #222222);
    box-shadow: 0 0 0 2px rgba(34, 34, 34, 0.08);
  }
`;

const StateList = styled.div`
  max-height: 230px;
  margin-top: 8px;
  padding: 4px;
  overflow-y: auto;
  border: 1px solid var(--zyvgo-border, #e1e5e9);
  border-radius: var(--zyvgo-radius-md, 8px);
  background: #fff;
`;

const StateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 11px;
  border-radius: 6px;
  background: ${(props) => (
    props.$active ? "#f4f4f5" : props.$selected ? "#eff6ff" : "transparent"
  )};
  cursor: pointer;

  &:hover {
    background: #f4f4f5;
  }
`;

const StateFlag = styled.img`
  width: auto;
  max-width: 30px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 2px;
  object-fit: cover;
  background: #f1f5f9;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
`;

const StateName = styled.span`
  flex: 1;
  color: var(--zyvgo-text-primary, #222222);
  font-size: 0.9rem;
`;

const StateCode = styled.span`
  color: #9ca3af;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
`;

const Check = styled.span`
  color: #2563eb;
  font-size: 1rem;
  font-weight: 700;
`;

const EmptyState = styled.div`
  padding: 16px;
  color: #9ca3af;
  font-size: 0.875rem;
  text-align: center;
`;

const Availability = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-top: 16px;
  padding: 12px 14px;
  border: 1px solid ${(props) => (
    props.$available === true
      ? "#bbf7d0"
      : props.$available === false
        ? "#fecaca"
        : "#dbe4ee"
  )};
  border-radius: 8px;
  background: ${(props) => (
    props.$available === true
      ? "#f0fdf4"
      : props.$available === false
        ? "#fef2f2"
        : "#f8fafc"
  )};
  color: ${(props) => (
    props.$available === true
      ? "#166534"
      : props.$available === false
        ? "#991b1b"
        : "#475569"
  )};
  font-size: 0.875rem;
  line-height: 1.5;
`;

const normalizeCodes = (values) => [...new Set(
  (Array.isArray(values) ? values : [])
    .map((state) => String(state || "").trim().toUpperCase())
    .filter((state) => US_STATE_CODES.includes(state)),
)];

const StateRouting = ({
  question,
  value,
  onChange,
  error,
  previewStates = null,
  authoritativeAvailability = null,
}) => {
  const [availableStates, setAvailableStates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statesError, setStatesError] = useState(null);
  const [query, setQuery] = useState(US_STATE_NAMES[value] || "");
  const [activeIndex, setActiveIndex] = useState(-1);
  const rowRefs = useRef([]);

  useEffect(() => {
    if (Array.isArray(previewStates) || previewStates) {
      const projectedStates = normalizeCodes(
        Array.isArray(previewStates)
          ? previewStates
          : previewStates.servableStates || previewStates.servable_states,
      );
      const projectedAllStates = normalizeCodes(
        Array.isArray(previewStates)
          ? projectedStates
          : previewStates.allStates || previewStates.all_states,
      );
      setAvailableStates({
        servable_states: projectedStates,
        all_states: projectedAllStates.length
          ? projectedAllStates
          : projectedStates,
      });
      setLoading(false);
      setStatesError(null);
      return undefined;
    }
    const fetchStates = async () => {
      try {
        setLoading(true);
        setStatesError(null);
        const data = await api.getAvailableStates();
        setAvailableStates(data);
      } catch (err) {
        console.error("Failed to fetch available states:", err);
        setStatesError(err.message || "Failed to load available states");
      } finally {
        setLoading(false);
      }
    };
    fetchStates();
    return undefined;
  }, [previewStates]);

  useEffect(() => {
    if (value && US_STATE_NAMES[value]) setQuery(US_STATE_NAMES[value]);
  }, [value]);

  const servableStates = useMemo(
    () => normalizeCodes(availableStates?.servable_states),
    [availableStates],
  );
  const allStates = useMemo(() => {
    // An explicitly supplied projection is authoritative. An empty list can
    // mean that the published Program has no usable checkout coverage; do not
    // widen it back to every U.S. state and send the patient into a loop.
    if (availableStates && Array.isArray(availableStates.all_states)) {
      return normalizeCodes(availableStates.all_states);
    }
    const configured = normalizeCodes(availableStates?.all_states);
    return configured.length ? configured : [...US_STATE_CODES];
  }, [availableStates]);
  const filteredStates = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery || (value && query === US_STATE_NAMES[value])) {
      return allStates;
    }
    return allStates.filter((code) => (
      code.toLowerCase().includes(normalizedQuery)
      || US_STATE_NAMES[code].toLowerCase().includes(normalizedQuery)
    ));
  }, [allStates, query, value]);

  const authoritativeState = normalizeCodes([
    authoritativeAvailability?.state,
  ])[0] || "";
  const hasAuthoritativeAvailability = Boolean(
    value
    && authoritativeState === value
    && typeof authoritativeAvailability?.available === "boolean"
  );
  const selectedAvailable = hasAuthoritativeAvailability
    ? authoritativeAvailability.available
    : Boolean(value && servableStates.includes(value));
  const treatmentName = question?.service_area_name || "This treatment";
  const selectState = (code) => {
    setQuery(US_STATE_NAMES[code]);
    setActiveIndex(filteredStates.indexOf(code));
    onChange(code);
  };
  const handleKeyDown = (event) => {
    if (!filteredStates.length) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      const next = activeIndex < 0
        ? (direction > 0 ? 0 : filteredStates.length - 1)
        : (activeIndex + direction + filteredStates.length) % filteredStates.length;
      setActiveIndex(next);
      rowRefs.current[next]?.scrollIntoView({ block: "nearest" });
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      selectState(filteredStates[activeIndex]);
    } else if (event.key === "Escape") {
      setActiveIndex(-1);
    }
  };

  if (loading) {
    return <Container><LoadingSpinner text="Loading available states..." /></Container>;
  }
  if (statesError) {
    return <Container><ErrorCard message={statesError} variant="error" /></Container>;
  }

  return (
    <Container>
      <FieldLabel htmlFor={`${question.id}_state_search`}>
        Your state <Required>*</Required>
      </FieldLabel>
      <SearchInput
        id={`${question.id}_state_search`}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded="true"
        aria-controls={`${question.id}_state_list`}
        aria-activedescendant={
          activeIndex >= 0 ? `${question.id}_${filteredStates[activeIndex]}` : undefined
        }
        autoComplete="off"
        placeholder="Type your state, or pick from the list…"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setActiveIndex(-1);
          if (value) onChange("");
        }}
        onKeyDown={handleKeyDown}
      />
      <StateList id={`${question.id}_state_list`} role="listbox">
        {filteredStates.length ? filteredStates.map((code, index) => (
          <StateRow
            id={`${question.id}_${code}`}
            key={code}
            ref={(node) => { rowRefs.current[index] = node; }}
            role="option"
            tabIndex={-1}
            aria-selected={value === code}
            $selected={value === code}
            $active={activeIndex === index}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => selectState(code)}
          >
            <StateFlag src={US_STATE_FLAG_DATA[code]} alt="" />
            <StateName>{US_STATE_NAMES[code]}</StateName>
            <StateCode>{code}</StateCode>
            {value === code && <Check aria-hidden="true">✓</Check>}
          </StateRow>
        )) : (
          <EmptyState>
            {allStates.length
              ? <>No states match “{query}”.</>
              : "No serviceable states are currently available for this treatment."}
          </EmptyState>
        )}
      </StateList>

      {value && (
        <Availability
          $available={hasAuthoritativeAvailability ? selectedAvailable : null}
          role="status"
        >
          <span aria-hidden="true">
            {hasAuthoritativeAvailability ? (selectedAvailable ? "✓" : "×") : "i"}
          </span>
          <span>
            {hasAuthoritativeAvailability && selectedAvailable ? (
              <><strong>{treatmentName}</strong> is available in {value}. You&apos;re all set. Tap Continue.</>
            ) : hasAuthoritativeAvailability ? (
              authoritativeAvailability?.message || (
                <>Unfortunately <strong>{treatmentName}</strong> isn&apos;t available in {value} yet. We&apos;re expanding state by state.</>
              )
            ) : (
              <>Select your state and tap Continue to check whether <strong>{treatmentName}</strong> is available.</>
            )}
          </span>
        </Availability>
      )}

      {error && <ErrorCard message={error} variant="error" />}
    </Container>
  );
};

export default StateRouting;
